import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { useCallback, useState } from "react";
import {
  MaterialLimitsEditor,
  FrontendMaterialLimit,
} from "components/common/forms/MaterialLimitsEditor";
import { useTenantTranslation } from "hooks/formatters";
import {
  MaterialConstraintClass,
  NameBasedMaterialLimit,
  NameBasedMaterialLimitBounds,
} from "store/api/generatedApi";
import { ConditionalMaterialLimits } from "./ruleParameters";
import { MaterialLimitSummary } from "./MaterialLimitSummary";
import { Limits } from "src/utils/addNewLimit";

const boundsTuple = (bounds: NameBasedMaterialLimitBounds): Limits => [
  bounds.min_mass,
  bounds.soft_min_mass,
  bounds.soft_max_mass,
  bounds.max_mass,
];
const toEditor = (
  limit: NameBasedMaterialLimit,
  classes: MaterialConstraintClass[]
): FrontendMaterialLimit => ({
  name: limit.name ?? "",
  hardness: limit.hardness,
  coefficients: Object.fromEntries(
    limit.coefficients.flatMap((coefficient) => {
      const materialClass = classes.find(
        (materialClass) =>
          materialClass.name === coefficient.material_constraint_class_name
      );
      return materialClass ? [[materialClass.id, coefficient.coefficient]] : [];
    })
  ),
  mixLimits:
    limit.bounds
      .filter((bounds) => bounds.basket_number === null)
      .map(boundsTuple)[0] ?? null,
  baskets: Object.fromEntries(
    limit.bounds.flatMap((bounds) =>
      bounds.basket_number !== null
        ? [
            [
              bounds.basket_number,
              {
                basketId: bounds.basket_number,
                number: bounds.basket_number,
                volume: 0,
                limits: boundsTuple(bounds),
              },
            ],
          ]
        : []
    )
  ),
});
const toBounds = (
  basket_number: number | null,
  limits: Limits
): NameBasedMaterialLimitBounds => ({
  basket_number,
  min_mass: limits[0],
  soft_min_mass: limits[1],
  soft_max_mass: limits[2],
  max_mass: limits[3],
});
const fromEditor = (
  edited: FrontendMaterialLimit,
  original: NameBasedMaterialLimit,
  classes: MaterialConstraintClass[]
): NameBasedMaterialLimit => ({
  ...original,
  name: edited.name || null,
  hardness: edited.hardness,
  coefficients: Object.entries(edited.coefficients).flatMap(
    ([id, coefficient]) => {
      const materialClass = classes.find(
        (materialClass) => materialClass.id === Number(id)
      );
      return materialClass
        ? [{ material_constraint_class_name: materialClass.name, coefficient }]
        : [];
    }
  ),
  bounds: [
    ...(edited.mixLimits ? [toBounds(null, edited.mixLimits)] : []),
    ...Object.values(edited.baskets).map((basket) =>
      toBounds(basket.number, basket.limits)
    ),
  ],
});
export const ConditionalLimitsEditor = ({
  rules,
  classes,
  onChange,
  disabled,
}: {
  rules: ConditionalMaterialLimits[];
  classes: MaterialConstraintClass[];
  onChange: (rules: ConditionalMaterialLimits[]) => void;
  disabled: boolean;
}) => {
  const { t } = useTenantTranslation();
  const [selection, setSelection] = useState<{
    rule: number;
    limit: number;
    draft: NameBasedMaterialLimit;
  } | null>(null);
  const updateDraft = useCallback(
    (edited: FrontendMaterialLimit) => {
      setSelection((previous) =>
        previous
          ? { ...previous, draft: fromEditor(edited, previous.draft, classes) }
          : null
      );
    },
    [classes]
  );
  return (
    <Stack gap={2}>
      {rules.map((rule, ruleIndex) => (
        <Stack key={rule.rule_index} gap={1}>
          {rule.limits.map((limit, limitIndex) => (
            <Stack
              key={limitIndex}
              direction="row"
              justifyContent="space-between"
              gap={1}
            >
              <Stack gap={0.5}>
                <Typography>
                  {t(limit.chef_group_name)} —{" "}
                  {t(limit.name ?? "materialLimitOverrides")}
                </Typography>
                <MaterialLimitSummary limit={limit} />
              </Stack>
              <Button
                disabled={
                  disabled ||
                  limit.coefficients.some(
                    (coefficient) =>
                      !classes.some(
                        (materialClass) =>
                          materialClass.name ===
                          coefficient.material_constraint_class_name
                      )
                  )
                }
                onClick={() => {
                  setSelection({
                    rule: ruleIndex,
                    limit: limitIndex,
                    draft: limit,
                  });
                }}
              >
                {t("edit")}
              </Button>
            </Stack>
          ))}
        </Stack>
      ))}
      {rules.length === 0 ? (
        <Typography color="text.secondary">{t("noData")}</Typography>
      ) : null}
      <Dialog
        open={selection !== null}
        onClose={() => {
          setSelection(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{t("materialLimitOverrides")}</DialogTitle>
        <DialogContent>
          {selection ? (
            <MaterialLimitsEditor
              key={`${selection.rule}-${selection.limit}`}
              materialLimit={toEditor(selection.draft, classes)}
              materialConstraintClasses={{
                status: "success",
                data: classes,
                fetching: false,
              }}
              onChange={updateDraft}
            />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setSelection(null);
            }}
          >
            {t("cancel")}
          </Button>
          <Button
            disabled={disabled}
            onClick={() => {
              if (selection)
                onChange(
                  rules.map((rule, index) =>
                    index === selection.rule
                      ? {
                          ...rule,
                          limits: rule.limits.map((limit, index) =>
                            index === selection.limit ? selection.draft : limit
                          ),
                        }
                      : rule
                  )
                );
              setSelection(null);
            }}
          >
            {t("apply")}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};
